// ============================================
// FILE: src/components/fashion/StarRating.jsx
// ============================================
//
// Read-only star display, and an interactive picker for the review form.
// Half-stars are rendered by clipping a filled star over an empty one, so an
// average of 4.3 reads honestly rather than rounding to 4.

import React, { useState } from 'react';
import { Star } from 'lucide-react';

const FILL = 'var(--fx-green)';
const EMPTY = '#d4d5d9';

export const StarRating = ({ value = 0, size = 14, className = '' }) => {
  const rating = Math.max(0, Math.min(5, Number(value) || 0));

  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`} aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = Math.max(0, Math.min(1, rating - (star - 1)));   // 0..1 of this star

        return (
          <span key={star} className="relative inline-block" style={{ width: size, height: size }}>
            <Star size={size} style={{ color: EMPTY }} fill={EMPTY} className="absolute inset-0" />
            {filled > 0 && (
              <span
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${filled * 100}%` }}
              >
                <Star size={size} style={{ color: FILL }} fill={FILL} />
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
};

/** Interactive 1–5 picker for the review form. */
export const StarPicker = ({ value = 0, onChange = () => {}, size = 32 }) => {
  const [hover, setHover] = useState(0);
  const shown = hover || value;

  const LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

  return (
    <div>
      <div className="flex items-center gap-1.5" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            className="p-0.5 transition-transform hover:scale-110"
            aria-label={`${star} star${star > 1 ? 's' : ''}`}
            aria-pressed={value === star}
          >
            <Star
              size={size}
              style={{ color: star <= shown ? FILL : EMPTY }}
              fill={star <= shown ? FILL : EMPTY}
            />
          </button>
        ))}
      </div>
      {shown > 0 && (
        <p className="mt-1.5" style={{ fontSize: 13, fontWeight: 700, color: 'var(--fx-ink-2)' }}>
          {LABELS[shown]}
        </p>
      )}
    </div>
  );
};

export default StarRating;
